import { createApi, fetchBaseQuery, retry } from "@reduxjs/toolkit/query/react"
import { inDevelopMode } from "utils/env"

import { configs } from "configs"
import { getAppLanguage } from "configs/useInitializeApp"
import { getDeviceInoHash, removeAppCredentials } from "configs/utils"
import { AUTH_TOKEN_COOKIE_KEY } from "constants"
import { getCookie } from "utils/cookie"
import { logout } from "./appSlice"

const authHeader = auth => ({ token: auth ? auth : getCookie(AUTH_TOKEN_COOKIE_KEY) })

const getErrorFromResponse = response => {
  console.error("Request Error: ", response)
  return {
    ...response,
    error: {
      status: response.error?.originalStatus || response.error?.status,
      // TODO:
      message:
        (response.error?.data?.errors?.[0]?.extra?.filename == "IncentivesErrorFile" &&
          response.error?.data?.errors?.[0]) ||
        response.error?.message ||
        response.error?.data?.message ||
        (response.error?.data?.errors && response.error.data.errors.map(e => `${e.message}`).join(", ")) ||
        response.error?.data ||
        "Network error",
    },
  }
}

const tagTypes = [
  "client-detail",
  "leads",
  "taskLogs",
  "sharings",
  "project-info",
  "clients",
  "kpi-reports",
  "meeting-list",
  "meeting-detail",
  "action-items",
  "project-detail",
  "qc-listing",
]

export const getHeaders = fingerPrint => {
  return {
    prepareHeaders: (headers, { getState }) => {
      let obj = {}
      const cookie = getCookie(AUTH_TOKEN_COOKIE_KEY)
      const stateToken = getState().app.auth.token || cookie
      if (stateToken && typeof stateToken !== "boolean") {
        obj = authHeader(stateToken)
      }
      Object.keys(obj).forEach(e => {
        !!obj[e] && headers.set(e, obj[e])
      })
      const locale = getState()?.app?.configs?.locale || getAppLanguage()?.key
      if (locale) {
        headers.set("x-lang", locale)
      }
      if (fingerPrint) {
        headers.set("x-di", fingerPrint)
      }
      return headers
    },
  }
}

const baseQuery = fingerPrint =>
  fetchBaseQuery({
    baseUrl: configs.apiBaseURL(),
    ...getHeaders(fingerPrint),
    credentials: inDevelopMode ? undefined : "include",
  })

const getRetryBaseQuery = fingerPrint =>
  retry(baseQuery(fingerPrint), {
    maxRetries: 0, // globally set the max-retries to zero so we can selectively set retry count for specific end points.
  })

const parentApi = createApi({
  reducerPath: "parentApi",
  baseQuery: async (args, api, extraOptions) => {
    try {
      const fingerprint = getDeviceInoHash()
      const retryableQuery = getRetryBaseQuery(fingerprint)

      const result = await retryableQuery(args, api, extraOptions)

      if (result.error) {
        const status = result.error.status ?? result?.error?.originalStatus
        if (status === 401) {
          removeAppCredentials(null, true)
          api.dispatch(logout(true))
          api.dispatch(parentApi.util.resetApiState())
        } else {
          return getErrorFromResponse(result)
        }
      }

      return result
    } catch (e) {
      return getErrorFromResponse(e)
    }
  },

  tagTypes,
  endpoints: builder => ({
    getUserDetail: builder.query({
      query: () => `/users/getProfile`,
      transformResponse: response => {
        return {
          ...response,
          SalesEventUsers: response?.SalesEventUsers?.map(({ SalesEvent, ...rest }) => ({
            ...rest,
            ...SalesEvent,
          })),
        }
      },
    }),

    getUserPrivileges: builder.query({
      query: () => `/authentication/selfPrivileges`,
    }),

    getUserAccounts: builder.query({
      query: () => `/userAccounts`,
    }),

    getPendingAgencyAssessments: builder.query({
      query: () => `/userAgencyAssessments/pending`,
    }),

    getPendingAnnouncements: builder.query({
      query: () => `/announcementRecipients`,
    }),

    getAgencySettingsByKeys: builder.query({
      query: () =>
        `/agency/settingByKeys?settingKeys=["role_privileges","payment_attachment_image_processing","client_stages","lead_stages","email_password_login","default_roster"]`,
    }),

    getAgencyPrimaryCurrency: builder.query({
      query: () => `/agency/currencies`,
      transformResponse: response => response.filter(item => item["key"] === "primary_currency"),
    }),

    getUserQuestionnaire: builder.query({
      query: ({ userId }) => `/users/${userId}/profileQuestionnaire`,
    }),

    signLeadAcknowledgeSlip: builder.mutation({
      query: ({ id }) => ({ url: `/user-signoff/${id}/sign`, method: "PUT", body: {} }),
    }),

    skipLeadAcknowledge: builder.mutation({
      query: id => ({ url: `/user-signoff/${id}/skipCount`, method: "PUT", body: {} }),
    }),

    updatePendingAnnouncements: builder.mutation({
      query: payload => ({ url: `/announcementRecipients`, method: "PUT", body: payload }),
    }),
    unblockOnWhatsappQR: builder.mutation({
     query: (body) => ({ url: `/whatsAppChats/verifyQRCode`, method: "PUT", body: body }),
    }),
  }),
})

export default parentApi
